export function isBomContent(text: string): boolean {
  return /\|\s*Part|bill of materials/i.test(text);
}

export interface ParsedMarkdownTable {
  /** Header cells, trimmed. */
  header: string[];
  /** Body rows — same column count as `header`, padded with "" if short. */
  rows: string[][];
  /** Prose before the table block (trimmed). */
  before: string;
  /** Prose after the table block (trimmed). */
  after: string;
}

const TABLE_LINE = /^\s*\|/;
const SEPARATOR_LINE = /^\s*\|?\s*:?-{2,}/;

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

/**
 * Extract the first GFM-style markdown table from `content` and return it
 * as structured rows alongside the prose that surrounded it. Returns null
 * when no header+separator pair is found.
 */
export function parseMarkdownTable(
  content: string,
): ParsedMarkdownTable | null {
  const lines = content.split("\n");
  for (let i = 0; i < lines.length - 1; i++) {
    if (!TABLE_LINE.test(lines[i])) continue;
    if (!SEPARATOR_LINE.test(lines[i + 1])) continue;
    const header = splitRow(lines[i]);
    let end = i + 2;
    while (
      end < lines.length &&
      TABLE_LINE.test(lines[end]) &&
      !SEPARATOR_LINE.test(lines[end])
    ) {
      end++;
    }
    const rows = lines.slice(i + 2, end).map((line) => {
      const cells = splitRow(line);
      // Pad / trim so every row matches the header arity — keeps the
      // grid rectangular if the LLM omitted a trailing pipe on a row.
      while (cells.length < header.length) cells.push("");
      return cells.slice(0, header.length);
    });
    return {
      header,
      rows,
      before: lines.slice(0, i).join("\n").trim(),
      after: lines.slice(end).join("\n").trim(),
    };
  }
  return null;
}

export function markdownTableToCsv(markdown: string): string {
  const rows: string[][] = [];
  for (const line of markdown.split("\n")) {
    if (
      line.startsWith("|") &&
      !line.includes("---") &&
      !line.startsWith("### ")
    ) {
      const cells = line
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((c) => c.trim());
      rows.push(cells);
    }
  }

  return rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap if needed
          const escaped = cell.replace(/"/g, '""');
          return /[,"\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(","),
    )
    .join("\n");
}

export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
